/* @flow */
/* eslint-env es6,browser */

import _idpbase from './idpbase';
import {
  IDP_PUBMATIC,
  USER_ID_CRITEO,
  USER_ID_FABRICK,
  USER_ID_LIVERAMP,
  USER_ID_UNIFIEDID,
  USER_ID_LOTAMEPANORAMA,
  USER_ID_PARRABLE,
  USER_ID_PUBCOMMON
} from './idpconstants';
import { loadJS } from '../utils/browserutil';
import { isCCPAOptedOut } from '../utils/ccpautils';
import { isFunction, isObject } from '../utils/coreutils';
import getCVars from '../utils/gputils';
import { logDebug } from '../utils/logger';

// Key map for individual IDs in Ids object returned from PubMatic Identity Hub
const PUBMATIC_IDP_KEYS = {
  [USER_ID_CRITEO]: 'criteoId',
  [USER_ID_PUBCOMMON]: 'pubcid',
  [USER_ID_LIVERAMP]: 'idl_env',
  [USER_ID_UNIFIEDID]: 'tdid',
  [USER_ID_LOTAMEPANORAMA]: 'lotamePanoramaId',
  [USER_ID_PARRABLE]: 'parrableId',
  [USER_ID_FABRICK]: 'fabrickId'
};

/**
 * @description Implement functionality to include pubmatic script and get Module IDs
 * @module pubmaticidp
 */
class _pubmaticidp extends _idpbase {
  _scriptLoaded: boolean;

  _enabledProviders: Array<string>;

  constructor(name: string = '') {
    super(name);
    this._scriptLoaded = false;
    this._enabledProviders = [];
  }

  updateConfig(config: _multiIDIDPConfig) {
    super.updateConfig(config);
    this._enabledProviders = config.enabledProviders || [];
    this._postUpdateConfig();
  }

  _postUpdateConfig() {
    if (isCCPAOptedOut() || this._scriptLoaded) {
      return;
    }
    // Full url along with the Publisher/profile ID comes from config
    loadJS(this._url, this._doc, true);
    this._scriptLoaded = true;
  }

  /**
   * @param {string} idpKey - Additional key required to fetch individual ID value for multi ID providers
   * @returns {string} ID value
   */
  getID(idpKey: string = ''): string {
    if (
      !idpKey.length ||
      !this._enabledProviders.includes(idpKey) ||
      !isObject(window.PWT) ||
      !isFunction(window.PWT.getUserIds)
    ) {
      return '';
    }
    const IDs = window.PWT.getUserIds();
    // Get response key from Map and return the value
    return IDs[PUBMATIC_IDP_KEYS[idpKey] || ''] || '';
  }
}

const pubmaticidp: _pubmaticidp = new _pubmaticidp(IDP_PUBMATIC);

export default pubmaticidp;

/**
 * Ref: https://community.pubmatic.com/display/IDS/Identity+Solutions#IdentitySolutions-Publisherspassingcustomfirst-partydatatoconsumers
 *
 * @description Expose global function for first party ID
 * @returns {string} SHA256 User Hash
 */
function getFirstPartyUserHash(): string {
  logDebug(`Called getFirstPartyUserHash [ _get_pub_idhub_fpdata ]`);
  // don't return user hash if opted out
  if (isCCPAOptedOut()) {
    return '';
  }

  const cvars = getCVars();
  const { _uh6 = '' } = cvars;
  return _uh6;
}

// eslint-disable-next-line camelcase
window._get_pub_idhub_fpdata =
  window._get_pub_idhub_fpdata || getFirstPartyUserHash;
